/**
 * 비밀번호 다시 확인의 5분 막힘을 이 탭이 기억한다 — 내 비밀번호 바꾸기(XM-08)와 사고 두 번째 문(XM-58)은 서버가 계정마다
 * **한 셈**으로 센다(계약 XM-08·XM-58 2026-10-13).
 *
 * 정할 것 21·34·37(사장님 21번 표 「5회 실패 — 1단계 임시 잠금(5분~15분간 입력 차단)」, 메인 세션 정리 '5번 → 5분 입력 막힘'):
 * 5번째 틀림부터 5분 동안 서버는 맞든 틀리든 429 `password_check_blocked` 만 주고 **남은 시간 칸은 없다.** 그래서 이 탭이
 * 처음 받은 429 의 때부터 5분을 센다. 앞서 다른 탭·다른 컴퓨터에서 막혔으면 실제보다 길게 보일 수는 있어도 짧게 보이지는
 * 않는다(막힌 동안 다시 받은 429 는 처음 때를 그대로 둔다).
 *
 * ⚠ **세션마다다.** 막힘은 그 계정의 것이다 — 로그아웃·다른 계정 로그인(세션 세대가 바뀜)에는 넘기지 않는다.
 */
import { useEffect, useState } from 'react'
import { sessionGeneration } from '@/auth/token'

/** 사장님 표 「5회 실패 — 1단계 임시 잠금(5분~15분간 입력 차단)」 → 5분(계약 XM-08). */
export const PASSWORD_CHECK_BLOCK_MS = 5 * 60_000

let block: { generation: number; until: number; message: string } | null = null

/** 막힘이 끝나는 때(밀리초). 막히지 않았거나 다른 세션이면 null. */
export function blockedUntil(now: number = Date.now()): number | null {
  if (!block || block.generation !== sessionGeneration() || block.until <= now) return null
  return block.until
}

/** 막힘의 서버 글(429 의 `message`). 막히지 않았으면 null. */
export function blockMessage(now: number = Date.now()): string | null {
  return blockedUntil(now) === null ? null : block?.message ?? null
}

/** 429 `password_check_blocked` 를 받았을 때. 이미 세고 있으면 그대로 — 처음 받은 때가 기준. */
export function noteBlocked(message: string, now: number = Date.now()): void {
  if (blockedUntil(now) !== null) return
  block = { generation: sessionGeneration(), until: now + PASSWORD_CHECK_BLOCK_MS, message }
}

/** 비밀번호가 맞았을 때(서버도 수를 0 으로 한다). */
export function clearBlocked(): void {
  block = null
}

/** 남은 초 → 「m:ss」. */
export function formatLeft(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * 막힌 동안의 남은 초 — 1초마다 다시 그린다. 막히지 않았으면 null.
 * `onBlocked(message)` 는 429 를 받은 화면이 부른다.
 */
export function usePasswordCheckBlock(): {
  seconds: number | null
  message: string | null
  onBlocked: (message: string) => void
} {
  const [, setTick] = useState(0)
  const until = blockedUntil()
  useEffect(() => {
    if (until === null) return
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [until])
  return {
    seconds: until === null ? null : Math.max(1, Math.ceil((until - Date.now()) / 1000)),
    message: blockMessage(),
    onBlocked: (message: string) => {
      noteBlocked(message)
      setTick((t) => t + 1)
    },
  }
}
